import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { AtsJobsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ActiveJobsLimitWarning } from 'modules/ats/components/ActiveJobsLimitWarning'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { FavouritesList } from './FavouriteList'
import JobsTable from './JobsTable'

const JobsPage = () => {
  const {
    company,
    access: { id: accessId },
  } = useCurrentSession()

  const { requisitionsEnabled } = useAtsContext()
  const { data, loading: loadingPage } = useQuery(AtsJobsPageDocument, {
    variables: { companyId: company.id, authorId: accessId },
  })
  const isRequisitionsFeatureEnabled = data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.name === Features.ats_job_requisitions
  )?.available
  const isRequisitionsEnabled = isRequisitionsFeatureEnabled && requisitionsEnabled
  const canUseRequisitions = data?.ats._policies.requisitionsUse.can
  const canCreateJobPostings = data?.ats._policies.createJobPostings.can && !isRequisitionsEnabled
  const canManageJobPostings = data?.ats._policies.manageJobPostings.can
  const canImportResources = data?.universalImporter?._policies.importResources.can
  const atsImportersFeatureFlag = data?.features.featuresConnection.nodes.find(
    (featureFlag) => featureFlag.name === 'ats_importers'
  )
  const showRequisitionsButton =
    isRequisitionsEnabled && canUseRequisitions && !canManageJobPostings
  const showCreateJobButton =
    (canManageJobPostings || (!isRequisitionsEnabled && canCreateJobPostings)) ?? false
  const showImportResources = !!(canImportResources && atsImportersFeatureFlag?.enabled)

  const [favouriteOperations, setFavouriteOperations] = useState<{
    adding: boolean
    deleting: number | null
  }>({
    adding: false,
    deleting: null,
  })

  const favouriteJobs = data?.ats.favouriteJobPostingsConnection.nodes || []

  const handleAddFavourite = (status: boolean) => {
    setFavouriteOperations((prev) => ({ ...prev, adding: status }))
  }

  const handleDeleteFavourite = (jobId: number | null) => {
    setFavouriteOperations((prev) => ({ ...prev, deleting: jobId }))
  }

  return (
    <ResponsivePage.Body variant="full" showHeader={false}>
      <Box gap="s16">
        <ActiveJobsLimitWarning />
        <Box gap="s20">
          <FavouritesList
            favouriteJobs={favouriteJobs}
            isAddingFavourite={favouriteOperations.adding}
            deletingFavouriteJob={favouriteOperations.deleting}
            onDeleteFavourite={handleDeleteFavourite}
            loading={loadingPage}
          />
          {/* TODO: move the features inside the component */}
          <JobsTable
            showRequisitionsButton={showRequisitionsButton ?? false}
            showCreateJobButton={showCreateJobButton ?? false}
            showImportResources={showImportResources ?? false}
            favouriteJobs={favouriteJobs}
            onAddFavourite={handleAddFavourite}
            onDeleteFavourite={handleDeleteFavourite}
          />
        </Box>
      </Box>
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default JobsPage
